import { useAuthStore } from '../../store/authStore';
import type { AuthUser, AuthSubsidiary, AuthBranch } from '../../lib/authClient';

/**
 * BranchPickerScreen — Phase 7.5.
 *
 * Shown after the user picks a subsidiary. Two render paths:
 *
 *   1. Subsidiary has branches → show the list, identical UX to subsidiary picker.
 *   2. Subsidiary has zero branches → empty state with a "Choose another company"
 *      link that returns to the subsidiary picker.
 *
 * The 1-branch auto-skip case never reaches this screen — authStore handles
 * it inside chooseSubsidiary().
 */
export function BranchPickerScreen({
  user,
  subsidiary,
  branches,
  busy,
}: {
  user: AuthUser;
  subsidiary: AuthSubsidiary;
  branches: AuthBranch[];
  busy: boolean;
}): JSX.Element {
  const error = useAuthStore((s) => s.error);
  const chooseBranch = useAuthStore((s) => s.chooseBranch);
  const backToSubsidiaryPicker = useAuthStore((s) => s.backToSubsidiaryPicker);
  const logout = useAuthStore((s) => s.logout);

  if (branches.length === 0) {
    return (
      <EmptyState
        user={user}
        subsidiary={subsidiary}
        onBack={backToSubsidiaryPicker}
        onLogout={() => logout()}
        busy={busy}
      />
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        color: 'var(--text)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 460,
          padding: 28,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <h1 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 600 }}>
            Choose a branch
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: 'var(--text-muted)',
            }}
          >
            <strong style={{ color: 'var(--text)' }}>{subsidiary.name}</strong>
            {' · '}
            <span>{user.username}</span>
          </p>
        </div>

        {error && (
          <div
            style={{
              marginBottom: 14,
              padding: '10px 12px',
              background: 'var(--red-light)',
              border: '1px solid var(--red-border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--red-text)',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {branches.map((b) => (
            <li key={b.id} style={{ marginBottom: 10 }}>
              <button
                onClick={() => void chooseBranch(b.id)}
                disabled={busy}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  textAlign: 'left',
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text)',
                  fontSize: 15,
                  cursor: busy ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
                onMouseEnter={(e) => {
                  if (!busy) {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--primary)';
                  }
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)';
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontWeight: 600 }}>{b.name}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {b.code && `Code: ${b.code}`}
                    {b.code && !b.has_cash_drawer && ' · '}
                    {!b.has_cash_drawer && (
                      <span style={{ color: 'var(--amber)' }}>No cash drawer</span>
                    )}
                  </span>
                </div>
                <span style={{ color: 'var(--text-muted)', fontSize: 18 }}>›</span>
              </button>
            </li>
          ))}
        </ul>

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button
            onClick={backToSubsidiaryPicker}
            disabled={busy}
            style={{
              flex: 1,
              padding: '10px 12px',
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-muted)',
              fontSize: 13,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            ← Choose company
          </button>
          <button
            onClick={() => logout()}
            disabled={busy}
            style={{
              flex: 1,
              padding: '10px 12px',
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-muted)',
              fontSize: 13,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({
  user,
  subsidiary,
  onBack,
  onLogout,
  busy,
}: {
  user: AuthUser;
  subsidiary: AuthSubsidiary;
  onBack: () => void;
  onLogout: () => void;
  busy: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        color: 'var(--text)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 460,
          padding: 28,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 40, marginBottom: 10 }}>🏚️</div>
        <h1 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 600 }}>
          No branches in this company
        </h1>
        <p
          style={{
            margin: '0 0 18px',
            fontSize: 13,
            color: 'var(--text-muted)',
            lineHeight: 1.5,
          }}
        >
          You don&apos;t have access to any branches in{' '}
          <strong style={{ color: 'var(--text)' }}>{subsidiary.name}</strong>.
          Pick another company or sign out.
        </p>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onBack}
            disabled={busy}
            style={{
              flex: 1,
              padding: '10px 12px',
              background: 'var(--primary)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--on-primary)',
              fontSize: 14,
              fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            Choose another company
          </button>
          <button
            onClick={onLogout}
            disabled={busy}
            style={{
              flex: 1,
              padding: '10px 12px',
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-muted)',
              fontSize: 14,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            Sign out
          </button>
        </div>

        <p
          style={{
            margin: '14px 0 0',
            fontSize: 12,
            color: 'var(--text-dim)',
          }}
        >
          Signed in as {user.username}
        </p>
      </div>
    </div>
  );
}
